import { api, Pagination, PaginationResponse } from './api';

export enum Feedback {
  REJECT = 0,
  LIKE = 1,
}

export enum SuggestionType {
  MATCH = 1,
  LIKE_YOU = 2,
}

export interface ScoreBreakdown {
  gated: boolean;
  gateReasons: string[];
  prefSatAtoB: number;
  prefSatBtoA: number;
  homophily: number;
  vibe: number | null;
  geo: number | null;
  height?: number | null;
  /** Ungated blend — what `final` would be if the gate passed. Admin-forced
   *  suggestions persist this as the score even when gated. */
  blended?: number;
  final: number;
}

export interface MatchEntity {
  id: number;
  userAId: string;
  userBId: string;
  /** Single blended vector-match score (replaces legacy scoreA/scoreB). */
  score?: number;
  /** Explainable score components from the vector scoring engine. */
  breakdown?: ScoreBreakdown | null;
  status?: number;
  reason?: string;
  createdAt?: string;
  updatedAt?: string;
  userB?: {
    id: string;
    email: string;
    profile?: any;
    photos?: Array<{
      id: string;
      sImage?: { url: string };
      mImage?: { url: string };
      lImage?: { url: string };
    }>;
  };
}

export interface SuggestionEntity {
  id: string;
  userId: string;
  matchId: number;
  isViewed: boolean;
  feedback?: Feedback;
  type: SuggestionType;
  reason?: string;
  /** Short one-liner shown in the chat-timeline suggestion bubble (card hook). */
  hook?: string;
  createdAt: string;
  updatedAt?: string;
  match?: MatchEntity;
  mutualLike?: boolean;
  userConversation?: {
    id: string;
  };
}

export type FindSuggestionsDto = Pagination;

export interface FindSuggestionsResponse extends PaginationResponse {
  data: SuggestionEntity[];
}

export interface CreateSuggestionDto {
  userId: string;
  targetUserId: string;
}

export interface CreateSuggestionResponse {
  success: boolean;
  message: string;
  data?: {
    suggestion?: SuggestionEntity;
    /** True when the admin-forced pair fails the normal hard gate. */
    gated?: boolean;
    gateReasons?: string[];
    /** Whether the suggestion card reached the user's matchmaker chat. */
    cardSent?: boolean;
  };
}

export interface AutoCreateSuggestionResponse {
  success: boolean;
  message: string;
  data?: {
    targetUserId?: string;
  };
}

export interface GenerateConversationResponse {
  success: boolean;
  message: string;
  data?: {
    userConversation?: {
      id: string;
      conversationId?: string;
    };
  };
}

export interface SendSuggestionCardResponse {
  success: boolean;
  message: string;
  data?: {
    messageId?: string;
    userConversationId?: string;
  };
}

export interface WinBackEligibleUser {
  userId: string;
  name: string | null;
  email: string | null;
  gender: number | null;
  age: number | null;
  city: string | null;
  country: string | null;
  lastRequestAt: string;
  lastSuggestionAt: string | null;
}

/** One city bucket for the location filter's chips. */
export interface WinBackCityFacet {
  city: string;
  country: string | null;
  count: number;
}

export interface WinBackPreviewResponse {
  success: boolean;
  data: {
    total: number;
    capped: boolean;
    users: WinBackEligibleUser[];
    cityFacets: WinBackCityFacet[];
  };
}

/** Preview request: dormancy window + optional advanced (profile-tab) filters. */
export interface WinBackPreviewArgs {
  inactiveDays: number;
  notSuggestedDays?: number;
  gender?: string;
  onboardingStep?: string;
  accountStatus?: string;
  membershipType?: string;
  userType?: string;
  interviewTopic?: string;
  ageMin?: string;
  ageMax?: string;
  search?: string;
  cities?: string[];
}

export interface WinBackApplyResponse {
  success: boolean;
  message: string;
  data?: { runId: string; dispatched: number };
}

export type WinBackAttemptStatus = 'pending' | 'sent' | 'no_result' | 'error';

export interface WinBackRunAttempt {
  userId: string;
  name: string | null;
  status: WinBackAttemptStatus;
  suggestionId: string | null;
  /** The suggested candidate's name (when a suggestion was delivered). */
  candidateName: string | null;
  /** Match score of the delivered suggestion, 0..1. */
  score: number | null;
  /** When the user was targeted (attempt created). */
  targetedAt: string;
  /** When the outcome was recorded (delivery time for SENT). */
  updatedAt: string;
}

export interface WinBackRunResponse {
  success: boolean;
  data: { runId: string; attempts: WinBackRunAttempt[] };
}

export interface WinBackRunSummary {
  runId: string;
  startedAt: string;
  total: number;
  pending: number;
  sent: number;
  noResult: number;
  error: number;
}

export interface WinBackRunsResponse {
  success: boolean;
  data: WinBackRunSummary[];
}

export const suggestionsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    adminFetchUserSuggestions: builder.query<
      FindSuggestionsResponse,
      { userId: string } & FindSuggestionsDto
    >({
      query: ({ userId, ...params }) => ({
        url: `/suggestions/admin/user/${userId}`,
        method: 'GET',
        params,
      }),
    }),
    adminCreateSuggestion: builder.mutation<
      CreateSuggestionResponse,
      CreateSuggestionDto
    >({
      query: ({ userId, targetUserId }) => ({
        url: '/suggestions/admin/create',
        method: 'POST',
        body: {
          userId,
          targetUserId,
        },
      }),
    }),
    adminGenerateConversation: builder.mutation<
      GenerateConversationResponse,
      { suggestionId: string }
    >({
      query: ({ suggestionId }) => ({
        url: `/suggestions/admin/${suggestionId}/generate-conversation`,
        method: 'POST',
      }),
    }),
    adminVectorMatch: builder.mutation<
      AutoCreateSuggestionResponse,
      { userId: string }
    >({
      query: ({ userId }) => ({
        url: '/suggestions/admin/vector-match',
        method: 'POST',
        body: { userId },
      }),
    }),
    adminSendSuggestionCard: builder.mutation<
      SendSuggestionCardResponse,
      { suggestionId: string }
    >({
      query: ({ suggestionId }) => ({
        url: `/suggestions/admin/${suggestionId}/send-card`,
        method: 'POST',
      }),
    }),
    adminRegenerateSuggestionReason: builder.mutation<
      {
        success: boolean;
        message?: string;
        data?: { hook: string; reason: string };
      },
      { suggestionId: string }
    >({
      query: ({ suggestionId }) => ({
        url: `/suggestions/admin/${suggestionId}/regenerate-reason`,
        method: 'POST',
      }),
    }),
    adminUpdateSuggestionReason: builder.mutation<
      { success: boolean },
      { suggestionId: string; hook: string; reason: string }
    >({
      query: ({ suggestionId, hook, reason }) => ({
        url: `/suggestions/admin/${suggestionId}`,
        method: 'PATCH',
        body: { hook, reason },
      }),
    }),
    adminWinBackPreview: builder.mutation<
      WinBackPreviewResponse,
      WinBackPreviewArgs
    >({
      query: (body) => ({
        url: '/suggestions/admin/win-back/preview',
        method: 'POST',
        body,
      }),
    }),
    adminWinBackApply: builder.mutation<
      WinBackApplyResponse,
      { userIds: string[] }
    >({
      query: (body) => ({
        url: '/suggestions/admin/win-back',
        method: 'POST',
        body,
      }),
    }),
    adminWinBackRun: builder.query<WinBackRunResponse, { runId: string }>({
      query: ({ runId }) => ({
        url: `/suggestions/admin/win-back/runs/${runId}`,
        method: 'GET',
      }),
    }),
    adminWinBackRuns: builder.query<WinBackRunsResponse, void>({
      query: () => ({
        url: '/suggestions/admin/win-back/runs',
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useAdminFetchUserSuggestionsQuery,
  useAdminCreateSuggestionMutation,
  useAdminGenerateConversationMutation,
  useAdminVectorMatchMutation,
  useAdminSendSuggestionCardMutation,
  useAdminRegenerateSuggestionReasonMutation,
  useAdminUpdateSuggestionReasonMutation,
  useAdminWinBackPreviewMutation,
  useAdminWinBackApplyMutation,
  useLazyAdminWinBackRunQuery,
  useAdminWinBackRunsQuery,
} = suggestionsApi;
